import { useState, FormEvent } from "react";
import { useAuth } from "../../../../contexts/AuthContext";
import { ProjectFileCategoryEnum, ProjectFileKindEnum } from "../../../../types/projectFile";
import { useProjectSiteInspections, type SiteInspectionItem, type DiaryEntryForSiteInspection } from "../../../../hooks/useProjectSiteInspections";
import { useCreateSiteInspection } from "../../../../hooks/useCreateSiteInspection";
import { useDeleteSiteInspection } from "../../../../hooks/useDeleteSiteInspection";
import { useCreateDiaryEntry } from "../../../../hooks/useCreateDiaryEntry";
import { useUpdateDiaryEntry } from "../../../../hooks/useUpdateDiaryEntry";
import { useDeleteDiaryEntry } from "../../../../hooks/useDeleteDiaryEntry";
import { useUploadProjectFile } from "../../../../hooks/useUploadProjectFile";
import { Modal, ConfirmDialog } from "../../../../components/shared";
import DiaryTabSkeleton from "./DiaryTabSkeleton";
import DiaryTabHeader from "./DiaryTabHeader";
import DiaryEntryForm, { type DiaryEntryFormData } from "./DiaryEntryForm";
import SiteInspections from "./SiteInspections";
import LogUploadModal from "./LogUploadModal";
import type { DiaryEntryItem } from "./DiaryEntryCard";

const initialFormData: DiaryEntryFormData = {
  date: new Date().toISOString().split("T")[0],
  description: "",
  tag: "",
  status: "normal",
  weather: "",
};

function entryToFormData(entry: DiaryEntryItem): DiaryEntryFormData {
  const dateStr =
    typeof entry.date === "string" && entry.date.length >= 10
      ? entry.date.slice(0, 10)
      : new Date(entry.date).toISOString().split("T")[0];
  const status =
    typeof entry.status === "string" ? entry.status.toLowerCase() : "normal";
  const tagDisplay = entry.tag && typeof entry.tag === "object" ? entry.tag.name : (entry.tag ?? "");
  return {
    date: dateStr,
    description: entry.description ?? "",
    tag: tagDisplay,
    status,
    weather: entry.weather ?? "",
  };
}

/** Map API shape to DiaryEntryItem for list/card. */
function toDiaryEntryItem(entry: DiaryEntryForSiteInspection): DiaryEntryItem {
  return {
    id: entry.id,
    date: entry.date,
    description: entry.description,
    tag: entry.tag ?? undefined,
    status: entry.status,
    weather: entry.weather ?? undefined,
    author: entry.author,
    projectFiles: entry.projectFiles ?? undefined,
    projectFileMetadata: entry.projectFileMetadata ?? undefined,
  };
}

export default function DiaryTab({ projectId }: { projectId: string }) {
  const { currentUser, isAdmin } = useAuth();
  const { siteInspections, loading, error: entriesError, refetch, hasNextPage, loadMore } = useProjectSiteInspections(projectId);
  const { createSiteInspection, loading: createVisitaLoading } = useCreateSiteInspection(projectId);
  const { deleteSiteInspection, loading: deleteVisitaLoading } = useDeleteSiteInspection(projectId);
  const { createDiaryEntry, loading: createLoading, error } = useCreateDiaryEntry();
  const { updateDiaryEntry, loading: updateLoading } = useUpdateDiaryEntry();
  const { deleteDiaryEntry, loading: deleteLoading } = useDeleteDiaryEntry();
  const { uploadProjectFile } = useUploadProjectFile(projectId);

  const [showCreateForm, setShowCreateForm] = useState(false);
  const [formData, setFormData] = useState<DiaryEntryFormData>(initialFormData);
  const [editingEntryId, setEditingEntryId] = useState<string | null>(null);
  const [selectedSiteInspectionId, setSelectedSiteInspectionId] = useState<string | null>(null);
  const [uploadModalEntryId, setUploadModalEntryId] = useState<string | null>(null);
  const [deleteConfirmEntry, setDeleteConfirmEntry] = useState<DiaryEntryItem | null>(null);
  const [deleteConfirmVisita, setDeleteConfirmVisita] = useState<SiteInspectionItem | null>(null);

  const handleFieldChange = (field: keyof DiaryEntryFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const isEdit = !!editingEntryId;
    try {
      if (isEdit) {
        await updateDiaryEntry({
          id: editingEntryId!,
          date: formData.date,
          description: formData.description,
          tagSlug: formData.tag || undefined,
          status: formData.status.toUpperCase(),
          weather: formData.weather || undefined,
        });
      } else if (selectedSiteInspectionId) {
        await createDiaryEntry({
          siteInspectionId: selectedSiteInspectionId,
          date: formData.date,
          description: formData.description,
          tagSlug: formData.tag || undefined,
          status: formData.status.toUpperCase(),
          weather: formData.weather || undefined,
        });
      }
      setShowCreateForm(false);
      setFormData(initialFormData);
      setEditingEntryId(null);
      setSelectedSiteInspectionId(null);
      refetch();
    } catch (err) {
      console.error(isEdit ? "Failed to update diary entry:" : "Failed to create diary entry:", err);
    }
  };

  const resetForm = () => {
    setShowCreateForm(false);
    setFormData(initialFormData);
    setEditingEntryId(null);
    setSelectedSiteInspectionId(null);
  };

  const handleUploadFiles = async (entryId: string, files: File[]) => {
    for (const file of files) {
      await uploadProjectFile({
        projectId,
        diaryEntryId: entryId,
        name: file.name,
        fileType: ProjectFileKindEnum.Document,
        category: ProjectFileCategoryEnum.Construction,
        attachment: file,
      });
    }
  };

  const handleAddVisita = async () => {
    try {
      await createSiteInspection();
      refetch();
    } catch (err) {
      console.error("Failed to add visita:", err);
    }
  };

  const handleOpenCreateForm = (siteInspection: SiteInspectionItem) => {
    setEditingEntryId(null);
    setSelectedSiteInspectionId(siteInspection.id);
    setFormData(initialFormData);
    setShowCreateForm(true);
  };

  const handleDeleteVisitaClick = (visita: SiteInspectionItem) => {
    setDeleteConfirmVisita(visita);
  };

  const handleDeleteVisitaConfirm = async () => {
    if (!deleteConfirmVisita) return;
    try {
      await deleteSiteInspection(deleteConfirmVisita.id);
      setDeleteConfirmVisita(null);
      refetch();
    } catch (err) {
      console.error("Failed to delete visita:", err);
    }
  };

  const handleEdit = (entry: DiaryEntryItem) => {
    setFormData(entryToFormData(entry));
    setEditingEntryId(entry.id);
    setShowCreateForm(true);
  };

  const handleDeleteClick = (entry: DiaryEntryItem) => {
    setDeleteConfirmEntry(entry);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirmEntry) return;
    try {
      await deleteDiaryEntry(deleteConfirmEntry.id);
      setDeleteConfirmEntry(null);
      refetch();
    } catch (err) {
      console.error("Failed to delete diary entry:", err);
    }
  };

  const formLoading = createLoading || updateLoading;

  if (loading) {
    return <DiaryTabSkeleton />;
  }

  if (entriesError) {
    return (
      <div className="space-y-8">
        <DiaryTabHeader
          isAdmin={isAdmin}
          onAddVisita={() => {}}
        />
        <div className="card-clean">
          <p className="text-red-600 dark:text-red-400" role="alert">
            Erro ao carregar entradas: {entriesError.message}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <DiaryTabHeader
        isAdmin={isAdmin}
        onAddVisita={handleAddVisita}
        addVisitaLoading={createVisitaLoading}
      />

      <Modal
        isOpen={showCreateForm}
        onClose={resetForm}
        maxWidth="max-w-2xl"
      >
        <DiaryEntryForm
          formData={formData}
          onFieldChange={handleFieldChange}
          onSubmit={handleSubmit}
          loading={formLoading}
          error={error ?? undefined}
          onCancel={resetForm}
          title={editingEntryId ? "EDITAR ENTRADA" : "NOVA ENTRADA"}
          variant="naked"
        />
      </Modal>

      <LogUploadModal
        isOpen={!!uploadModalEntryId}
        onClose={() => setUploadModalEntryId(null)}
        entryId={uploadModalEntryId ?? ""}
        onUploadFiles={handleUploadFiles}
        onComplete={() => {
          refetch();
          setUploadModalEntryId(null);
        }}
      />

      <SiteInspections
        siteInspections={siteInspections}
        hasNextPage={hasNextPage}
        onLoadMore={loadMore}
        isAdmin={isAdmin}
        currentUserId={currentUser?.id}
        projectId={projectId}
        onFileVisibilityChange={refetch}
        onAddFirstVisita={handleAddVisita}
        addVisitaLoading={createVisitaLoading}
        onAddEntry={handleOpenCreateForm}
        onDeleteVisita={handleDeleteVisitaClick}
        onEditEntry={handleEdit}
        onDeleteEntry={handleDeleteClick}
        onUploadEntry={(entry) => setUploadModalEntryId(entry.id)}
        toDiaryEntryItem={toDiaryEntryItem}
      />

      <ConfirmDialog
        isOpen={!!deleteConfirmEntry}
        onClose={() => setDeleteConfirmEntry(null)}
        title="Excluir entrada"
        message="Tem certeza que deseja excluir esta entrada? Esta ação não pode ser desfeita."
        confirmLabel="EXCLUIR"
        confirmLoadingLabel="EXCLUINDO..."
        onConfirm={handleDeleteConfirm}
        loading={deleteLoading}
        destructive
      />

      <ConfirmDialog
        isOpen={!!deleteConfirmVisita}
        onClose={() => setDeleteConfirmVisita(null)}
        title="Excluir vista"
        message={
          deleteConfirmVisita
            ? `Tem certeza que deseja excluir ${deleteConfirmVisita.displayLabel}? Esta ação não pode ser desfeita.`
            : ""
        }
        confirmLabel="EXCLUIR"
        confirmLoadingLabel="EXCLUINDO..."
        onConfirm={handleDeleteVisitaConfirm}
        loading={deleteVisitaLoading}
        destructive
      />
    </div>
  );
}
